import Api from './Api';
import { getSignedUrl } from './signedUrl';
import { useEffect, useState } from 'react';

/**
 * The Doxy patient no longer sends a corporate plan id when joining the
 * waiting room, so the SignalR push reaches the doctor without the plan's
 * name or card photos. The doctor side looks the plan up instead, from the
 * `UserId` the push does carry, and then exchanges the returned Firebase
 * paths for signed URLs (see `helpers/signedUrl`) so the cards can render.
 */

/** The endpoint identifies the patient by the same id SignalR calls `UserId`. */
export const corporatePlanPhotosUrl = (userId) =>
  `corporate-plans/patients/${userId}/photos`;

const EMPTY = { name: '', frontUrl: '', backUrl: '' };

/**
 * Load a patient's corporate plan by user id, ready to render.
 * Returns `{ name, frontUrl, backUrl }`, all empty while loading or when the
 * patient has no plan. Never raises a toast: a missing plan is normal.
 */
export const useCorporatePlanOfUser = (userId) => {
  const [plan, setPlan] = useState(EMPTY);

  useEffect(() => {
    if (!userId) {
      setPlan(EMPTY);
      return undefined;
    }

    let alive = true;
    (async () => {
      const { result, status } = await Api.get(
        corporatePlanPhotosUrl(userId),
        undefined,
        undefined,
        false
      );
      if (!alive) return;

      // The endpoint returns every plan on the account; the Verify ID popup
      // shows one card, so take the first.
      const picked = status === 200 ? result?.[0] : null;
      if (!picked) {
        setPlan(EMPTY);
        return;
      }

      const [frontUrl, backUrl] = await Promise.all([
        getSignedUrl(picked.frontPhoto),
        getSignedUrl(picked.backPhoto),
      ]);
      if (alive) setPlan({ name: picked.name || '', frontUrl, backUrl });
    })();

    return () => {
      alive = false;
    };
  }, [userId]);

  return plan;
};
